<template>
  <div class="article" v-if="post">
    <div class="lp-container article-wrap">
      <button class="back-btn" @click="$router.push('/blog')">
        <l-icon size="16">mdi-arrow-left</l-icon>{{ $t('blog.backToBlog') }}
      </button>

      <div class="article-meta">
        <span class="article-tag">{{ post.tag }}</span>
        <span class="article-dot">·</span>
        <span>{{ formattedDate }}</span>
        <span class="article-dot">·</span>
        <span>{{ post.readingTime }} {{ $t('blog.minRead') }}</span>
      </div>

      <h1 class="lp-h1 article-title lp-balance">{{ post.title }}</h1>
      <p class="lp-lead article-lead lp-balance">{{ post.description }}</p>

      <hr class="lp-hr article-rule" />

      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="article-body" v-html="bodyParts.before"></div>
      <figure v-if="bodyParts.mockup" class="article-figure">
        <app-screenshot :variant="bodyParts.mockup" :label="post.title" />
        <figcaption>{{ post.mockupCaption }}</figcaption>
      </figure>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="bodyParts.after" class="article-body" v-html="bodyParts.after"></div>

      <!-- CTA -->
      <div class="article-cta">
        <h3 class="lp-h3">{{ $t('blog.ctaTitle') }}</h3>
        <p class="article-cta__lead">{{ $t('blog.ctaSubtitle') }}</p>
        <router-link class="lp-btn lp-btn--primary lp-btn--lg" to="/download">
          <l-icon small color="white">mdi-download</l-icon>{{ $t('blog.ctaButton') }}
        </router-link>
      </div>

      <!-- Related -->
      <div v-if="related.length" class="related">
        <h3 class="related-title">{{ $t('blog.relatedTitle') }}</h3>
        <div class="related-grid">
          <router-link
            v-for="r in related"
            :key="r.slug"
            :to="`/blog/${r.slug}`"
            class="related-card"
          >
            <span class="related-icon"><l-icon size="18" color="primary">{{ r.icon }}</l-icon></span>
            <span class="related-name">{{ r.title }}</span>
            <span class="related-go">{{ $t('blog.readMore') }} <l-icon size="14">mdi-arrow-right</l-icon></span>
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="lp-container article-404">
    <h1 class="lp-h2">404</h1>
    <button class="lp-btn lp-btn--ghost" @click="$router.push('/blog')">{{ $t('blog.backToBlog') }}</button>
  </div>
</template>

<script>
import { POSTS, getPost, localized } from './posts';
import AppScreenshot from '@/components/mockup/AppScreenshot.vue';

// Un artículo puede intercalar un mockup de la app dejando este comentario en el
// cuerpo: <!--mockup:agent-->. Es un comentario HTML para que, si el cuerpo se
// vuelca en crudo en otro sitio (SEO, feeds), no se vea nada raro.
const MOCKUP_RE = /<!--\s*mockup:([a-z-]+)\s*-->/;

export default {
  name: 'BlogArticle',
  components: { AppScreenshot },
  metaInfo() {
    if (!this.post) return { title: 'Not found | Nukli' };
    return {
      title: this.post.title,
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.post.description },
        { name: 'keywords', content: this.post.keywords || '' },
        { property: 'og:title', content: this.post.title },
        { property: 'og:description', content: this.post.description },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: `https://nukliapp.com/blog/${this.post.slug}` },
        { property: 'article:published_time', content: this.post.date },
        { property: 'twitter:card', content: 'summary_large_image' },
      ],
      script: [{
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: this.post.title,
          description: this.post.description,
          datePublished: this.post.date,
          author: { '@type': 'Organization', name: 'Nukli' },
          publisher: { '@type': 'Organization', name: 'Nukli' },
          mainEntityOfPage: `https://nukliapp.com/blog/${this.post.slug}`,
        },
      }],
    };
  },
  computed: {
    post() {
      return localized(getPost(this.$route.params.slug), this.$i18n.locale);
    },
    bodyParts() {
      const raw = (this.post && this.post.body) || '';
      const m = raw.match(MOCKUP_RE);
      if (!m) return { before: raw, mockup: '', after: '' };
      return {
        before: raw.slice(0, m.index),
        mockup: m[1],
        after: raw.slice(m.index + m[0].length),
      };
    },
    related() {
      const locale = this.$i18n.locale;
      return POSTS.filter((p) => p.slug !== this.$route.params.slug)
        .slice(0, 2)
        .map((p) => localized(p, locale));
    },
    formattedDate() {
      try {
        return new Date(this.post.date).toLocaleDateString(this.$i18n.locale, {
          year: 'numeric', month: 'long', day: 'numeric',
        });
      } catch {
        return this.post.date;
      }
    },
  },
  watch: {
    '$route.params.slug'() {
      window.scrollTo({ top: 0 });
    },
  },
};
</script>

<style scoped>
.article { background: var(--lp-bg); min-height: calc(100vh - 64px); }
/* 64px del AppBar fijo + aire (v-main no lo compensa) */
.article-wrap { max-width: 760px; padding-top: 112px; padding-bottom: 96px; }

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 36px;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 0.925rem;
  color: var(--lp-muted);
  transition: color 0.15s ease;
}
.back-btn:hover { color: var(--lp-ink); }

.article-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--lp-muted);
  margin-bottom: 18px;
}
.article-tag {
  color: var(--lp-accent);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.article-dot { opacity: 0.5; }

.article-title { font-size: clamp(2.2rem, 4.5vw, 3.1rem); margin-bottom: 20px; }
.article-lead { margin-bottom: 32px; }
.article-rule { margin-bottom: 40px; }

.article-body {
  font-size: 1.075rem;
  line-height: 1.8;
  color: var(--lp-ink-2);
}
.article-body >>> h2 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.7rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--lp-ink);
  margin: 2.4rem 0 0.9rem;
}
.article-body >>> h3 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 1.6rem 0 0.6rem;
}
.article-body >>> p { margin: 0 0 1.15rem; }
.article-body >>> ul,
.article-body >>> ol { margin: 0 0 1.3rem 1.25rem; }
.article-body >>> li { margin-bottom: 0.55rem; }
.article-body >>> strong { color: var(--lp-ink); font-weight: 600; }

.article-figure {
  margin: 36px 0 40px;
  padding: 0;
}
.article-figure figcaption {
  margin-top: 14px;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--lp-muted);
}

.article-cta {
  margin-top: 64px;
  padding: 40px;
  text-align: center;
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.article-cta__lead { color: var(--lp-muted); margin: 0 0 14px; font-size: 1rem; }

.related { margin-top: 72px; }
.related-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 0 0 20px;
}
.related-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.related-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px;
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  background: var(--lp-surface);
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.related-card:hover { border-color: var(--lp-line-strong); transform: translateY(-3px); }
.related-icon {
  width: 36px; height: 36px;
  border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent-weak);
}
.related-name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.3;
  color: var(--lp-ink);
}
.related-go {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.85rem; font-weight: 600; color: var(--lp-accent);
}

.article-404 {
  text-align: center;
  padding: 120px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

@media (max-width: 599px) {
  .article-wrap { padding-top: 96px; }
  .related-grid { grid-template-columns: 1fr; }
  .article-cta { padding: 28px 20px; }
}
</style>
